<template>
  <BaseModal
    :heading="$t('admin.treatwell.address_modal.heading')"
    useForm
    small
    testId="task-provide-company-info"
    @submit="submit"
    @close="close"
  >
    <Flex
      direction="column"
      :class="[
        $style.base,
        {
          [$style.smallScreen]: $screen === 's'
        }
      ]"
    >
      <Flex
        :direction="$screen === 's' ? 'column' : 'row'"
        equalWidth
      >
        <div :class="$style.name">
          <BaseCard
            gray
            fullWidth
          >
            <BaseInput
              v-model="companyInfo.name"
              :label="$t('onboarding.new_tasks.company_info.company_name')"
              required
              :minLength="2"
              v-test="'company-name-input'"
            />
          </BaseCard>
        </div>
        <img
          src="/img/tasks/setup_company_details.svg"
          :class="$style.img"
        >
      </Flex>
      <BaseCard gray>
        <Flex direction="column">
          <Flex
            :direction="$screen === 's' ? 'column' : 'row'"
            equalWidth
          >
            <BaseInput
              v-model="companyInfo.postalcode"
              :label="$t('onboarding.new_tasks.company_info.postal')"
              required
              v-test="'company-postal-input'"
            />
            <BaseInput
              v-model="companyInfo.address"
              required
              :label="$t('onboarding.new_tasks.company_info.address')"
              v-test="'company-address-input'"
            />
          </Flex>
          <Flex
            :direction="$screen === 's' ? 'column' : 'row'"
            equalWidth
          >
            <BaseInput
              v-model="companyInfo.city"
              required
              :label="$t('onboarding.new_tasks.company_info.city')"
              v-test="'company-city-input'"
            />
            <BaseInput
              v-model="companyInfo.phone"
              required
              type="tel"
              :minLength="9"
              :maxLength="16"
              :error="
                isSubmitted && showPhoneError
                  ? `*${filters.capitalize($t('global.validations.invalid', { value: $t('global.phone_number') }))}`
                  : null
              "
              :label="$t('onboarding.new_tasks.company_info.phone_number')"
              v-test="'company-phone-input'"
            />
          </Flex>
        </Flex>
      </BaseCard>
      <Dac7BusinessDetails
        v-model:dac7Details="dac7Details"
        v-model:ownerUser="ownerUser"
        :country="company.country"
        :errors="dac7Errors"
      />
    </Flex>
    <template #footer>
      <BaseButton
        color="inverted"
        @click="close"
      >
        {{ $t('global.actions.cancel') }}
      </BaseButton>
      <BaseButton
        :loading="loading"
        submitForm
        v-test="'company-info-submit'"
      >
        {{ $t('global.actions.save') }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script
  lang="ts"
  setup
>
import { storeToRefs } from 'pinia';
import { reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import filters from '@/filters';
import { flash } from '@/helpers/ui';
import { useCompanyStore } from '@/stores/company';
import { useTreatwellStore } from '@/stores/treatwell';
import type { Dac7DetailsAttributes, OwnerUserAttributes } from '@/types';
import Dac7BusinessDetails from './Dac7BusinessDetails.vue';

const { t } = useI18n();
const router = useRouter();

const close = () => {
  router.push({
    name: 'treatwell-admin',
  });
};

const companyStore = useCompanyStore();
const { company } = storeToRefs(companyStore);
const { updateCompany } = companyStore;
const { getTreatwellStatus } = useTreatwellStore();
const loading = ref(false);
const isSubmitted = ref(false);
const showPhoneError = ref(false);

const companyInfo = reactive({
  name: company.value.name,
  postalcode: company.value.postalcode,
  city: company.value.city,
  phone: company.value.phone,
  address: company.value.address,
});

const dac7Details = ref<Dac7DetailsAttributes>({
  legalForm: company.value.dac7Details?.legalForm ?? '',
  businessRegistrationNumber:
    company.value.dac7Details?.businessRegistrationNumber ?? '',
  taxIdentificationNumber:
    company.value.dac7Details?.taxIdentificationNumber ?? '',
  vatNumber: company.value.dac7Details?.vatNumber ?? '',
});
const initialDac7Details = JSON.stringify(dac7Details.value);

const ownerUser = ref<OwnerUserAttributes>({
  firstName: company.value.ownerUser?.firstName ?? '',
  lastName: company.value.ownerUser?.lastName ?? '',
});
const initialOwnerUser = JSON.stringify(ownerUser.value);

const dac7Errors = ref<Record<string, string>>({});

watch(
  () => companyInfo.phone,
  () => {
    showPhoneError.value = false;
  },
);

const submit = () => {
  loading.value = true;
  isSubmitted.value = true;
  dac7Errors.value = {};

  const dac7Changed = JSON.stringify(dac7Details.value) !== initialDac7Details;
  const ownerUserChanged = JSON.stringify(ownerUser.value) !== initialOwnerUser;

  updateCompany({
    ...companyInfo,
    internalName: companyInfo.name,
    ...(dac7Changed ? { dac7Details: dac7Details.value } : {}),
    ...(ownerUserChanged ? { ownerUser: ownerUser.value } : {}),
  })
    .then(async () => {
      // Await this rather than firing it in the background: the company-info
      // task card on the dashboard we're about to close into is checked off by
      // treatwellStatus.dac7Detail, and closing before it resolves left the
      // refetch racing with whatever the user does next on the dashboard.
      await getTreatwellStatus();
      loading.value = false;
      flash(t('global.flash.company_updated'));
      close();
    })
    .catch((errors) => {
      if (
        errors?.length &&
        errors.find((error) => error.attribute === 'phone')
      ) {
        showPhoneError.value = true;
      }

      errors?.forEach((error) => {
        if (error.type === 'invalid') {
          dac7Errors.value[error.attribute] = t(
            'admin.treatwell.address_modal.dac7.errors.invalid_format',
          );
        }
      });

      loading.value = false;
    });
};
</script>

<style
  lang="scss"
  module
>
.base.smallScreen {
  img {
    display: none;
  }
}

.name {
  width: 100%;
  align-self: end;
}

.img {
  height: 150px;
  width: 200px;
}
</style>
